
import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReportLayout from "@/components/reports/ReportLayout";
import FilterCard from "@/components/reports/FilterCard";
import CollectionSummaryTable, { CollectionSummaryItem } from "@/components/reports/CollectionSummaryTable";
import ReportSummaryHeader from "@/components/reports/ReportSummaryHeader";

// Mock collection summary data
const INITIAL_SUMMARY_DATA: CollectionSummaryItem[] = [
  {
    id: 1,
    date: "2025-05-16",
    zone: "North",
    collector: "John Smith",
    totalAmount: 12500,
    transactions: 45,
    method: "Cash",
  },
  {
    id: 2,
    date: "2025-05-16",
    zone: "South",
    collector: "Emma Wilson",
    totalAmount: 9720,
    transactions: 36,
    method: "Online",
  },
  {
    id: 3,
    date: "2025-05-15",
    zone: "East",
    collector: "Michael Brown",
    totalAmount: 8650,
    transactions: 28,
    method: "Mixed",
  },
  {
    id: 4,
    date: "2025-05-15",
    zone: "West",
    collector: "Sarah Davis",
    totalAmount: 14200,
    transactions: 51,
    method: "Cash",
  },
  {
    id: 5,
    date: "2025-05-14",
    zone: "Central",
    collector: "David Miller",
    totalAmount: 6800,
    transactions: 22,
    method: "Online",
  },
];

const CollectionSummaryPage = () => {
  const [dateRange, setDateRange] = useState({
    from: "2025-05-14",
    to: "2025-05-16",
  });
  const [zone, setZone] = useState<string>("all");
  const [collector, setCollector] = useState<string>("all");
  const [summaryData, setSummaryData] = useState(INITIAL_SUMMARY_DATA);

  const totalAmount = summaryData.reduce(
    (sum, item) => sum + item.totalAmount,
    0
  );
  const totalTransactions = summaryData.reduce(
    (sum, item) => sum + item.transactions,
    0
  );

  const handleFilter = () => {
    // In a real app, this would filter based on the selected criteria
    // For this demo, we'll just shuffle the data to simulate filtering
    setSummaryData([...INITIAL_SUMMARY_DATA].sort(() => Math.random() - 0.5));
  };

  const handleReset = () => {
    setDateRange({ from: "2025-05-14", to: "2025-05-16" });
    setZone("all");
    setCollector("all");
    setSummaryData(INITIAL_SUMMARY_DATA);
  };

  return (
    <ReportLayout 
      title="Collection Summary Report"
      description="Analyze collection trends and performance"
    >
      <FilterCard 
        title="Report Filters"
        description="Set parameters to generate a specific report"
        onReset={handleReset}
        onFilter={handleFilter}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="from-date">From Date</Label>
            <div className="flex items-center relative">
              <Input
                id="from-date"
                type="date"
                value={dateRange.from}
                onChange={(e) =>
                  setDateRange({ ...dateRange, from: e.target.value })
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="to-date">To Date</Label>
            <div className="flex items-center relative">
              <Input
                id="to-date"
                type="date"
                value={dateRange.to}
                onChange={(e) =>
                  setDateRange({ ...dateRange, to: e.target.value })
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="zone">Zone</Label>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger>
                <SelectValue placeholder="All zones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All zones</SelectItem>
                <SelectItem value="North">North</SelectItem>
                <SelectItem value="South">South</SelectItem>
                <SelectItem value="East">East</SelectItem>
                <SelectItem value="West">West</SelectItem>
                <SelectItem value="Central">Central</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="collector">Collector</Label>
            <Select value={collector} onValueChange={setCollector}>
              <SelectTrigger>
                <SelectValue placeholder="All collectors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All collectors</SelectItem>
                <SelectItem value="John Smith">John Smith</SelectItem>
                <SelectItem value="Emma Wilson">Emma Wilson</SelectItem>
                <SelectItem value="Michael Brown">Michael Brown</SelectItem>
                <SelectItem value="Sarah Davis">Sarah Davis</SelectItem>
                <SelectItem value="David Miller">David Miller</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </FilterCard>

      <Card>
        <CardHeader>
          <ReportSummaryHeader
            title="Collection Summary"
            description={`Period: ${dateRange.from} to ${dateRange.to}${zone !== "all" ? ` • Zone: ${zone}` : ""}${collector !== "all" ? ` • Collector: ${collector}` : ""}`}
            totalAmount={totalAmount}
            additionalInfo={
              <div className="text-sm text-muted-foreground">
                {totalTransactions} transactions
              </div>
            }
          />
        </CardHeader>
        <CardContent>
          <CollectionSummaryTable data={summaryData} />
        </CardContent>
      </Card>
    </ReportLayout>
  );
};

export default CollectionSummaryPage;
